import {
  mobile,
  styled,
  Text,
  useIsMobile,
  useQuery,
  useRouter,
} from '@obvio/app'
import { Accordion, Button, Grid, GridItem, Loading, Stack } from '@obvio/ui'
import { ascSort, map, setDayEnd, setDayStart, setTime } from '@obvio/utils'
import { motion } from 'framer-motion'
import { useState } from 'react'

import { CenteredSection } from '../../CenteredSection'
import { Calendar } from '@/components/Calendar'
import { QUERIES } from '@/constants/queries'

import type { Event as EventType } from './Events'
import type { Dict } from '@obvio/utils'
import type { ReactElement } from 'react'

const Item = styled(GridItem)`
  width: 100%;
`

const EventsContent = styled(Stack)`
  height: 100%;
`

const CalendarSticky = styled(Calendar)`
  position: sticky;
  top: 0;
`

const Center = styled.div`
  ${(theme) => theme.flexCenter};
  height: 100%;
  width: 100%;
`

const DateNode = styled.span`
  font-size: 2.25rem;
  font-weight: 200;
  line-height: 100%;
  font-family: ${(theme) => theme.font};

  min-width: 8rem;
  padding-top: ${(theme) => theme.spacing.small};
  @media ${mobile} {
    padding-top: 0;
  }
`

const EventCategory = styled.span`
  font-size: 0.8125rem;
  color: #828282;
  font-weight: 300;
`

const Event = styled(Button)`
  font-size: 1rem;
  line-height: 150%;
  color: black;
  font-weight: 300;
  padding: 0 !important;
  margin-left: 0 !important;
  justify-content: flex-start;
  white-space: normal;
  text-align: left;
`

const Hour = styled.span`
  align-self: end;
  font-weight: 300;
`

const DateGrid = styled(Grid)`
  flex: 1;
  @media ${mobile} {
    grid-template-columns: 1fr;
    grid-gap: ${(theme) => theme.spacing.extraSmall};
  }
`
const DateContent = motion(DateGrid)

const StyledStack = styled(Stack)`
  flex: 1;
  @media ${mobile} {
    margin: 0 auto;
  }
`
const MotionStack = motion(StyledStack)

type Dates = [Date, Date] | [Date] | []

type EventsListProps = {
  events: EventType[]
}

const list = {
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.25,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      when: 'afterChildren',
    },
  },
}

const item = {
  visible: { opacity: 1, y: 0 },
  hidden: { opacity: 0, y: -10 },
}

const buildEvents = (events: EventType[]): Dict<EventType[]> =>
  map(
    (dayEvents) => dayEvents.sort((a, b) => ascSort(a.date, b.date)),
    events.reduce<Dict<EventType[]>>((acc, event) => {
      if (Number.isNaN(Date.parse(event.date as unknown as string))) {
        return acc
      }

      const [hrs, mins] = (event.startTime ?? '00:00')
        .split(':')
        .map((n) => Number.parseInt(n, 10))

      const date = setTime('Europe/Warsaw', event.date, hrs, mins, 0, 0)

      const day = date.getDate()
      const month = date.toLocaleString('default', { month: 'short' })
      const name = `${day} ${month}`

      return { ...acc, [name]: [...(acc[name] ?? []), { ...event, date }] }
    }, {}),
  )

function simpleDateFormat(
  date: Date | string | number,
  timeZone = 'Europe/Warsaw',
): string {
  return new Date(date).toLocaleDateString('en-CA', { timeZone })
}

function EventsList({ events }: EventsListProps): ReactElement {
  const isMobile = useIsMobile()
  const { setQuery } = useRouter()
  const eventsByDay = buildEvents(events)

  const hasEvents = Object.keys(eventsByDay).length > 0

  return (
    <EventsContent kind="vertical" divider spacing="medium">
      {hasEvents ? (
        Object.entries(eventsByDay).map(([day, dayEvents]) => (
          <Stack key={day} spacing={isMobile ? 'medium' : 'extraLarge'}>
            <DateNode>{day}</DateNode>
            <MotionStack
              initial="hidden"
              animate="visible"
              variants={list}
              kind="vertical"
              spacing="medium"
            >
              {dayEvents.map((event) => (
                <DateContent
                  key={day + event.id}
                  templateColumns="1fr 4fr"
                  gap="small"
                  variants={item}
                >
                  <Hour>{event.startTime}</Hour>
                  <Stack
                    kind="vertical"
                    spacing={isMobile ? '0.1rem' : 'extraSmall'}
                  >
                    <EventCategory>{event.category.title}</EventCategory>
                    <Event
                      onClick={() =>
                        setQuery({
                          id: event.slug,
                          date: simpleDateFormat(event.date),
                        })
                      }
                    >
                      {event.title}
                    </Event>
                  </Stack>
                </DateContent>
              ))}
            </MotionStack>
          </Stack>
        ))
      ) : (
        <Center>
          <Text>Brak wydarzeń w wybranym przedziale czasowym</Text>
        </Center>
      )}
    </EventsContent>
  )
}

const CalendarWrap = styled(Grid)`
  width: 100%;
  @media ${mobile} {
    grid-template-columns: auto;
  }
`

const CalendarSection = styled(CenteredSection)`
  width: 68rem;
  border-top: 1px solid ${(theme) => theme.stroke};
  border-bottom: 1px solid ${(theme) => theme.stroke};
  margin-top: ${(theme) => theme.spacing.extraLarge};

  @media ${mobile} {
    max-width: 100%;
  }
`

const EventsItem = styled(Item)`
  margin: 0 ${(theme) => theme.spacing.small};
  @media ${mobile} {
    margin: 0;
    padding: 0 ${(theme) => theme.spacing.extraSmall};
  }
`

const CalendarTitle = styled.span`
  letter-spacing: 12%;
  font-weight: 400;
`

export function EventsCalendar(): ReactElement {
  const [dates, setDates] = useState<Dates>([])
  const from = setDayStart(dates[0] ?? new Date())
  const to = setDayEnd(dates[1] ?? from)

  const { data } = useQuery('getEvents', {
    select: QUERIES['events-calendar'].getEvents,
    ...(dates.length > 0 && { args: { byDate: { gte: from, lte: to } } }),
  })

  const isMobile = useIsMobile()

  return (
    <CalendarSection>
      <CalendarWrap templateColumns="auto minmax(0,1fr)" gap="extraLarge">
        <Item noCenter>
          {isMobile ? (
            <Accordion>
              <Accordion.Element
                title={<CalendarTitle>KALENDARIUM</CalendarTitle>}
              >
                <Calendar selectRange allowPartialRange onChange={setDates} />
              </Accordion.Element>
            </Accordion>
          ) : (
            <CalendarSticky
              locale="pl-PL"
              selectRange
              allowPartialRange
              onChange={(v: Dates) => setDates(v)}
            />
          )}
        </Item>
        <EventsItem noCenter>
          {data ? <EventsList events={data} /> : <Loading />}
        </EventsItem>
      </CalendarWrap>
    </CalendarSection>
  )
}
